// internal deps
import { VALID_PAYMENT_GROUP_TYPES, VALID_WALLET_TYPES } from "@swiggy-private/payment-core";
import { Option } from "@swiggy-private/js-fp";
import { Some } from "@swiggy-private/js-fp";
import {
    IWalletPaymentGroup,
    PaymentGroupDataDef,
    PaymentMethod,
    PaymentMethodMeta,
    WalletPaymentMethodData,
    WalletPaymentMethodMeta,
} from "@swiggy-private/payment-ui";
import { UserContextValue } from "@swiggy-private/checkout-core";
import { format } from "@swiggy-private/core-ui";
import { WALLET_CODES } from "@swiggy-private/payment-types";
import { SendLinkAccountStatusRequestPayload } from "@swiggy-private/native-bridge";
import { getBridgeSdk } from "@swiggy-private/checkout-core";

// interfaces
import { OrderData } from "@swiggy-private/payment-types";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { AmazonPayLinkWalletParams } from "@payment-client/interfaces/WalletPublic";
import { AmazonPayLinkWalletResponseData } from "@swiggy-private/native-bridge";

// helpers
import {
    getAllMethodsOfGroup,
    getPaymentMethodFromGroup,
    getPaymentMethodIconUrl,
    isPreferredGroup,
} from "./PaymentHelper";

//constants
import { LINK_WALLET, WALLET_DISPLAY_NAMES } from "@payment-client/constants/Wallet";
import { isTurboPaymentGroup } from "./TurboPayment";

export const generatePhonepeRedirectUrl = (order: OrderData): string => {
    return order.phonepeDebitRequisites?.webRedirectionUrl ?? "";
};

export function getAllWalletMethods(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): WalletPaymentMethodData[] {
    return [
        ...getAllMethodsOfGroup(paymentGroups, VALID_PAYMENT_GROUP_TYPES.WALLET),
        ...getAllMethodsOfGroup(paymentGroups, VALID_PAYMENT_GROUP_TYPES.WALLET_WEB),
    ];
}

export function isWalletGroup(group: PaymentGroupDataDef): group is IWalletPaymentGroup["paymentData"] {
    return (
        group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.WALLET ||
        group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.WALLET_WEB
    );
}

export function findWalletPaymentMethod(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    walletCode: string,
): Option<WalletPaymentMethodData> {
    const walletPaymentMethod = Some(paymentGroups.find(isWalletGroup)).andThen((group) =>
        getPaymentMethodFromGroup(group, walletCode),
    );

    return walletPaymentMethod.orElse(() =>
        Some(paymentGroups.find((group) => isPreferredGroup(group) || isTurboPaymentGroup(group))).andThen((group) =>
            getPaymentMethodFromGroup(group, walletCode),
        ),
    );
}

export const isWalletPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is WalletPaymentMethodData => {
    return (
        [VALID_PAYMENT_GROUP_TYPES.WALLET, VALID_PAYMENT_GROUP_TYPES.WALLET_WEB].indexOf(
            method.groupName.toUpperCase(),
        ) !== -1
    );
};

export const isRedirectFlow = (methodData: WalletPaymentMethodData): boolean => {
    return methodData.name.toUpperCase() === VALID_WALLET_TYPES.AMAZONPAY;
};

export const canRenderWallet = (
    user: UserContextValue["user"],
    paymentMethod: PaymentMethod<WalletPaymentMethodMeta>,
): boolean => !(user.isNone() && !isRedirectFlow(paymentMethod));

export const isAmazonPayMethodAvailable = (paymentResponse: GetPaymentMethodsResponse): boolean => {
    const method = findWalletPaymentMethod(paymentResponse.paymentGroup, VALID_WALLET_TYPES.AMAZONPAY);

    if (method.isNone()) {
        return false;
    }
    return method.unwrap().enabled;
};

export const isNewAmazonPaySDKAvailable = (paymentResponse: GetPaymentMethodsResponse): boolean | undefined => {
    const method = findWalletPaymentMethod(paymentResponse.paymentGroup, VALID_WALLET_TYPES.AMAZONPAY);
    if (method.isNone()) {
        return false;
    }

    return method.unwrap().meta?.isNewApaySdk;
};

export const isAmazonPayLinked = (paymentResponse: GetPaymentMethodsResponse): boolean | undefined => {
    const method = findWalletPaymentMethod(paymentResponse.paymentGroup, VALID_WALLET_TYPES.AMAZONPAY);
    if (method.isNone()) {
        return false;
    }
    return method.unwrap().meta?.isApayLinked;
};

export const getAmazonPayBalanceFromPaymentsResponse = (
    paymentResponse: GetPaymentMethodsResponse,
): string | undefined => {
    const method = findWalletPaymentMethod(paymentResponse.paymentGroup, VALID_WALLET_TYPES.AMAZONPAY);
    if (method.isNone()) {
        return "";
    }
    return method.unwrap().balance;
};

export const getLinkWalletPageTitle = (walletCode: WALLET_CODES): string => {
    const walletDisplayName = WALLET_DISPLAY_NAMES[walletCode];
    return format(LINK_WALLET, walletDisplayName);
};

export const sendLinkAccountStatusToNative = (payload: SendLinkAccountStatusRequestPayload) => (): void => {
    getBridgeSdk()
        .sendLinkAccountStatusToNative(payload)
        .catch((err) => {
            __DEV__ || console.error(err);
        });
};

export const getWalletDetailsFromPaymentResponse = (
    paymentResponse: GetPaymentMethodsResponse | null | undefined,
    walletCode: string,
): { iconUrl: string; paymentCode: string } | undefined => {
    if (!paymentResponse) {
        return;
    }
    const paymentMethod = findWalletPaymentMethod(paymentResponse.paymentGroup, walletCode).unwrap();
    const iconUrl = getPaymentMethodIconUrl(paymentMethod);

    return {
        iconUrl: iconUrl,
        paymentCode: paymentMethod.paymentCode,
    };
};
export const getAddMoneyAndDelinkWalletPageTitle = (walletCode: WALLET_CODES): string =>
    `${WALLET_DISPLAY_NAMES[walletCode]} Account`;

export const shouldEnableAddMoneyToWallet = (walletCode: WALLET_CODES): boolean =>
    ![WALLET_CODES.amazonpay, WALLET_CODES.phonepe, WALLET_CODES.paytmpostpaid, WALLET_CODES.paytm].includes(
        walletCode,
    );

export const shouldShowBalance = (walletCode: WALLET_CODES): boolean =>
    ![WALLET_CODES.simpl, WALLET_CODES.paytmpostpaid].includes(walletCode);

/* eslint-disable @typescript-eslint/naming-convention */
export const buildAmazonPayLinkParams = (res: AmazonPayLinkWalletResponseData): AmazonPayLinkWalletParams => ({
    code: res.authCode,
    client_id: res.clientId,
    redirect_uri: res.redirectUri,
    code_verifier: res.codeVerifier,
});
/* eslint-enable @typescript-eslint/naming-convention */

export const getWalletCodes = (walletCode: WALLET_CODES): string[] => {
    switch (walletCode) {
        case WALLET_CODES.paytm:
        case WALLET_CODES.paytmpostpaid:
            return [WALLET_CODES.paytm, WALLET_CODES.paytmpostpaid];
        default:
            return [walletCode];
    }
};
